import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';

export interface SearchBarProps {
  onSearch: (query: string) => void;
  initialValue?: string;
  placeholder?: string;
  debounceMs?: number;
  isLoading?: boolean;
  className?: string;
  /**
   * Optional external ref to the input element. When provided, the SearchBar
   * still uses its own internal ref for focus restoration but mirrors the
   * element into this ref so callers can imperatively focus / scroll the input.
   */
  inputRef?: React.Ref<HTMLInputElement>;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  initialValue = '',
  placeholder = 'Search...',
  debounceMs = 800,
  isLoading = false,
  className,
  inputRef: externalInputRef,
}) => {
  const [query, setQuery] = useState(initialValue);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInProgressRef = useRef(false);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof externalInputRef === 'function') {
        externalInputRef(node);
      } else if (externalInputRef) {
        (externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [externalInputRef]
  );

  // Handle debounced search (skip on initial mount if no initial value)
  useEffect(() => {
    // Skip debounce on initial mount if no initial value
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!query) {
        return;
      }
    }

    if (!query) {
      onSearch('');
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchInProgressRef.current = true;
      onSearch(query);
      // Restore focus after search - don't set searchInProgressRef to false here,
      // let the isLoading useEffect handle it
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
              inputRef.current.focus();
            }
          });
        });
      }, 100);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, onSearch, debounceMs]);

  // Handle immediate search on Enter
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      console.log('Enter pressed, current query:', query);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      console.log('Calling onSearch with query:', query);
      searchInProgressRef.current = true;
      onSearch(query);
      // Restore focus after search - don't set searchInProgressRef to false here,
      // let the isLoading useEffect handle it
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
              inputRef.current.focus();
            }
          });
        });
      }, 100);
    }
  }, [query, onSearch]);

  // Handle clear
  const handleClear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setQuery('');
    // Call onSearch immediately without debounce when clearing
    onSearch('');
    // Keep focus on input after clearing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  }, [onSearch]);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  // Restore focus when loading completes (search finished)
  useEffect(() => {
    if (!isLoading && searchInProgressRef.current) {
      // Search just completed, restore focus
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        searchInProgressRef.current = false;
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  // Handle blur - restore focus if search is in progress
  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Don't restore focus if clicking on interactive elements
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && (
      relatedTarget.closest('.search-bar-clear') ||
      relatedTarget.closest('button') ||
      relatedTarget.closest('a') ||
      relatedTarget.closest('input') ||
      relatedTarget.closest('select')
    )) {
      searchInProgressRef.current = false;
      return;
    }
    // Re-focus if search is in progress
    if (searchInProgressRef.current) {
      setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, []);

  return (
    <form
      className={clsx('search-bar', className)}
      aria-busy={isLoading}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Form submit, current query:', query);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        console.log('Calling onSearch from form submit with query:', query);
        searchInProgressRef.current = true;
        onSearch(query);
        // Restore focus after search - don't set searchInProgressRef to false here,
        // let the isLoading useEffect handle it
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (inputRef.current && document.activeElement !== inputRef.current) {
                inputRef.current.focus();
              }
            });
          });
        }, 100);
      }}
    >
      <div
        className={clsx('search-bar-input-wrapper', query && 'search-bar-input-wrapper--has-clear')}
      >
        <input
          ref={setInputRef}
          type="text"
          className="search-bar-input"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-label="Search"
        />
        {query && (
          <button
            type="button"
            className="search-bar-clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>
    </form>
  );
};
